import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ruler } from 'lucide-react';

const sizeData = {
  tops: {
    title: 'Tops, T-Shirts & Sweaters',
    headers: ['Size', 'Chest (in)', 'Waist (in)', 'Length (in)'],
    rows: [
      ['XS', '32-34', '26-28', '26'],
      ['S', '34-36', '28-30', '27'],
      ['M', '38-40', '32-34', '28'],
      ['L', '42-44', '36-38', '29'],
      ['XL', '46-48', '40-42', '30'],
      ['XXL', '50-52', '44-46', '31'],
    ],
  },
  bottoms: {
    title: 'Pants, Jeans & Shorts',
    headers: ['Size', 'Waist (in)', 'Hip (in)', 'Inseam (in)'],
    rows: [
      ['28', '28', '36', '30'],
      ['30', '30', '38', '30'],
      ['32', '32', '40', '32'],
      ['34', '34', '42', '32'],
      ['36', '36', '44', '32'],
      ['38', '38', '46', '32'],
    ],
  },
  kids: {
    title: 'Kids (Boys & Girls)',
    headers: ['Size', 'Age', 'Height (in)', 'Chest (in)'],
    rows: [
      ['4', '3-4 yrs', '39-41', '22-23'],
      ['6', '5-6 yrs', '43-46', '24-25'],
      ['8', '7-8 yrs', '48-51', '26-27'],
      ['10', '9-10 yrs', '52-55', '28-29'],
      ['12', '11-12 yrs', '56-59', '30-31'],
    ],
  },
  babies: {
    title: 'Babies',
    headers: ['Size', 'Age', 'Weight (lbs)', 'Height (in)'],
    rows: [
      ['0-3M', '0-3 months', '5-12', '17-23'],
      ['3-6M', '3-6 months', '12-16', '23-26'],
      ['6-12M', '6-12 months', '16-22', '26-29'],
      ['12-18M', '12-18 months', '22-27', '29-32'],
      ['18-24M', '18-24 months', '27-30', '32-34'],
    ],
  },
  hats: {
    title: 'Hats',
    headers: ['Size', 'Head Circumference (in)'],
    rows: [
      ['S/M', '21-22'],
      ['L/XL', '22.5-24'],
      ['One Size', '22-23 (adjustable)'],
    ],
  },
};

export default function SizeGuidePage() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            <span className="font-semibold">Size Guide</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-center mb-12">
            <Ruler className="w-12 h-12 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              SIZE GUIDE
            </h1>
            <p className="text-[#525252] max-w-lg mx-auto">
              Find your perfect fit with our comprehensive size guide. All measurements are in inches.
            </p>
          </div>

          {/* How to Measure */}
          <div className="bg-[#FDF0D5] border-2 border-[#0A0A0A] p-6 mb-12">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              How to Measure
            </h2>
            <ul className="space-y-2 text-sm">
              <li><strong>Chest:</strong> Measure around the fullest part of your chest, keeping the tape level.</li>
              <li><strong>Waist:</strong> Measure around your natural waistline, keeping the tape comfortably loose.</li>
              <li><strong>Hip:</strong> Measure around the fullest part of your hips.</li>
              <li><strong>Inseam:</strong> Measure from the crotch to the bottom of the leg.</li>
            </ul>
          </div>

          {/* Size Tables */}
          <div className="space-y-12">
            {Object.entries(sizeData).map(([key, data]) => (
              <div key={key} className="border-2 border-[#0A0A0A] bg-white" data-testid={`size-table-${key}`}>
                <div className="p-4 border-b-2 border-[#0A0A0A] bg-[#FAD4D4]">
                  <h3 className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {data.title}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-[#0A0A0A]">
                        {data.headers.map((header, idx) => (
                          <th
                            key={idx}
                            className="text-left p-4 text-sm font-semibold uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className={rowIdx !== data.rows.length - 1 ? 'border-b border-[#E5E5E5]' : ''}
                        >
                          {row.map((cell, cellIdx) => (
                            <td
                              key={cellIdx}
                              className={`p-4 text-sm ${cellIdx === 0 ? 'font-semibold' : ''}`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Tips */}
          <div className="mt-12 bg-[#D4E2FA] border-2 border-[#0A0A0A] p-6">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Fit Tips
            </h2>
            <ul className="space-y-2 text-sm">
              <li>• If you're between sizes, we recommend sizing up for a more relaxed fit.</li>
              <li>• Our tops are designed with a classic fit unless otherwise noted.</li>
              <li>• For kids, consider sizing up to allow room for growth.</li>
              <li>• Washing instructions: Follow care labels to maintain the size and shape of your garments.</li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-[#525252] mb-4">Still not sure? Our customer service team is here to help!</p>
            <Link
              to="/products"
              className="inline-block bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] px-8 py-3 font-semibold hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
            >
              Shop Now
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
